import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { SISCard, SISCallout, SISInfluence, SISTakeaway, SISNoteImage } from "@/components/sis/SISLessonShell";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";

export default function SouthIndian9() {
  const { lesson, course, accent } = findLesson("south-indian-songbook", "9");
  return (
    <LessonShell
      course={course}
      courseSlug="south-indian-songbook"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <YouTubeEmbed
        videoId="tEoSaLOUG1E"
        title="En Vaanilay — Ilaiyaraaja · Johnny (1980)"
        caption="Three different flavours of B minor in one song. Listen for the 6th and 7th scale degrees — those two notes carry all the emotional switching."
      />

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/09-en-vaanilay-page1.png"
        caption="Jason's chart — four minor scales on B (natural ♭3 ♭6 ♭7, harmonic ♭3 ♭6, Dorian ♭3 ♭7, melodic ♭3), then the song in 6/8: Bm–Em–Bm marked natural, D–E–D marked Dorian, and the F♯ marked harmonic. Below: B minor's parallel major (B), and that key's relative minor, G♯ minor."
      />

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/09-en-vaanilay-page2.png"
        caption="Page 2 — the chord chart with melody fragments: Bm–Em–Bm, D–E–D–F♯, Bm G Em F♯, then a passage in the parallel major, B, with Em → B marked as a plagal cadence and a move to G♯ minor."
      />

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/09-en-vaanilay-page3.png"
        caption="Page 3 — a chain of minor 3rds (D–F–A♭–B) and another chord passage: C♯m, Amaj7, Gm, Cmaj7, B7."
      />

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/09-en-vaanilay-page4.png"
        caption="Page 4 — the flat keys: each major key beside its relative minor, with key signatures."
      />

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/09-en-vaanilay-page5.png"
        caption="Page 5 — the sharp keys: each major key beside its relative minor, with key signatures."
      />

      <SISCard label="The big idea" title="Three minors, one song — Ilaiyaraaja's lighting rig">
        <p>
          Every Western musician learns "B minor" as if it's one thing. Ilaiyaraaja uses it as three different things — and in <em>En Vaanilay</em>, he switches between all three within a single song, using each one for a different emotional beat.
        </p>
        <p>
          Jason's summary: <em>"The meat of the song, main ingredients of the song is a kind of B minor where it's mostly natural and harmonic with a flavor or a spice of Dorian."</em> Same tonic (B), different moods, produced by altering just two notes — the 6th and the 7th scale degree.
        </p>
        <p>
          In Carnatic terms, each version is a different raga on the same Sa. Natabhairavi, Kiravani and Kharaharapriya are the 20th, 21st and 22nd Melakartas — neighbours in the same chakra, sharing Ri, Ga and Ma and differing only in Dha and Ni, which are exactly the 6th and 7th.
        </p>
      </SISCard>

      <SISCard label="Scale anatomy" title="The three B minors — what changes, what stays">
        <p>
          All three scales share: B – C♯ – D – E – F♯. The only differences are in the 6th (G or G♯) and 7th (A or A♯):
        </p>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-[14px]">
            <thead>
              <tr className="border-b border-amber-700/20 text-left text-[11px] uppercase tracking-wider text-indigo-800">
                <th className="pb-2 pr-4">Scale</th>
                <th className="pb-2 pr-4">6th degree</th>
                <th className="pb-2 pr-4">7th degree</th>
                <th className="pb-2 pr-4">Carnatic raga</th>
                <th className="pb-2">Emotional quality</th>
              </tr>
            </thead>
            <tbody className="space-y-1">
              <tr className="border-b border-amber-700/10">
                <td className="py-2 pr-4 font-semibold">B Natural Minor (Aeolian)</td>
                <td className="py-2 pr-4">G♮ (♭6)</td>
                <td className="py-2 pr-4">A♮ (♭7)</td>
                <td className="py-2 pr-4">Natabhairavi</td>
                <td className="py-2">Sad, resigned, earthbound</td>
              </tr>
              <tr className="border-b border-amber-700/10">
                <td className="py-2 pr-4 font-semibold">B Harmonic Minor</td>
                <td className="py-2 pr-4">G♮ (♭6)</td>
                <td className="py-2 pr-4">A♯ (♮7, raised)</td>
                <td className="py-2 pr-4">Kiravani</td>
                <td className="py-2">Dark tension, urgent, yearning</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-semibold">B Dorian</td>
                <td className="py-2 pr-4">G♯ (♮6, raised)</td>
                <td className="py-2 pr-4">A♮ (♭7)</td>
                <td className="py-2 pr-4">Kharaharapriya</td>
                <td className="py-2">Bittersweet, almost hopeful</td>
              </tr>
            </tbody>
          </table>
        </div>
      </SISCard>

      <RomanNumeralAnalysis
        label="Natural minor (Aeolian) — Natabhairavi · from Jason's chart"
        caption="Bm → Em → Bm, which Jason labels natural minor. The Em chord (E G B) carries G♮, the ♭6 — the note that separates natural and harmonic minor from Dorian."
        chords={[
          { chord: "Bm", rn: "i", function: "Tonic", pitches: ["B3","D4","F#4","B4"] },
          { chord: "Em", rn: "iv", function: "Pre-dominant", pitches: ["E3","G3","B3","E4"] },
          { bar: true },
          { chord: "Bm", rn: "i", function: "Tonic", pitches: ["B3","D4","F#4"] },
        ]}
      />

      <RomanNumeralAnalysis
        label="Dorian — Kharaharapriya · from Jason's chart"
        caption="D → E → D, which Jason labels Dorian. The E major chord (E G♯ B) carries G♯ — the raised 6th of B minor, the Dorian fingerprint. Note that it is a major IV, where natural minor would give Em."
        chords={[
          { chord: "D", rn: "III", function: "Mediant", pitches: ["D3","F#3","A3","D4"] },
          { chord: "E", rn: "IV", function: "Pre-dominant", pitches: ["E3","G#3","B3","E4"] },
          { bar: true },
          { chord: "D", rn: "III", function: "Mediant", pitches: ["D3","F#3","A3","D4"] },
        ]}
      />

      <RomanNumeralAnalysis
        label="Harmonic minor — Kiravani · from Jason's chart"
        caption="Then F♯ major, which Jason labels harmonic. Its A♯ is the raised 7th of B minor — only harmonic (or melodic) minor gives a major V chord, and that A♯ pulls upward to B. Later in the chart the same idea returns as Bm · G · Em · F♯."
        chords={[
          { chord: "Bm", rn: "i", function: "Tonic", pitches: ["B3","D4","F#4"] },
          { chord: "G", rn: "VI", function: "Submediant", pitches: ["G3","B3","D4","G4"] },
          { chord: "Em", rn: "iv", function: "Pre-dominant", pitches: ["E3","G3","B3"] },
          { chord: "F♯", rn: "V", function: "Dominant", pitches: ["F#3","A#3","C#4","F#4"] },
        ]}
      />

      <SISInfluence
        artists={[
          "Miles Davis — 'So What' (D Dorian)",
          "Santana — 'Oye Como Va' (A Dorian)",
        ]}
      >
        <p>
          Miles Davis's 'So What' is a classic demonstration of Dorian — its A sections sit in D Dorian and let the raised 6th breathe. Santana's 'Oye Como Va' grooves on A Dorian (the raised 6th there is F♯). Compare either with a natural-minor ballad and the difference in colour is immediately audible.
        </p>
      </SISInfluence>

      <SISCallout label="Practice drill — three scales, one melody">
        <p>
          Learn this ascending phrase in B: B – C♯ – D – E – F♯ – [?] – [?] – B. Now play it three ways:
          (1) G♮ – A♮ (natural minor / Aeolian — dark and resigned)
          (2) G♮ – A♯ (harmonic minor / Kiravani — tense and urgent)
          (3) G♯ – A♮ (Dorian / Kharaharapriya — bittersweet and open).
          Sing the phrase each time. The two notes G and A are doing almost all the emotional work. Changing them is changing the lighting on the same scene.
        </p>
      </SISCallout>

      <SISTakeaway>
        <p>
          Learning that "minor" is not one scale but a family of related scales — Aeolian, harmonic minor, Dorian, melodic minor, Phrygian — is one of the most transformative insights in music theory. Ilaiyaraaja uses three of them in one song, and raga thinking makes that natural: each is a different raga on the same Sa. In raga thinking, a small change in one swara creates an entirely different emotional world. That sensitivity — treating individual scale degrees as independent levers for emotion — is what separates composing from copying. Practise all three minor scales daily until you can switch between them by ear, not by theory.
        </p>
      </SISTakeaway>
    </LessonShell>
  );
}
